import { create } from "zustand";
import * as Sentry from "@sentry/react";
import {
  getOnboardingState,
  saveOnboardingState,
  saveOnboardingStateDebounced,
} from "./api";
import {
  createInitialOnboardingState,
  type ChecklistTaskId,
  type OnboardingAnswers,
  type OnboardingState,
  type OnboardingStep,
} from "./types";
import { trackOnboarding } from "./analytics";

type OnboardingStore = {
  state: OnboardingState | null;
  /** True once the initial server fetch has settled (success or failure). */
  loaded: boolean;
  /** True if the initial fetch errored — the gate stays closed in that case. */
  loadFailed: boolean;
  load: () => Promise<void>;
  /** Create + persist the initial state. Called once by the gate on trigger. */
  begin: () => void;
  setAnswers: (answers: OnboardingAnswers) => void;
  setStep: (step: OnboardingStep) => void;
  setSelectedWatchlists: (ids: number[]) => void;
  markCompleted: (appliedTemplateId: string) => void;
  dismiss: () => void;
  markTaskDone: (taskId: ChecklistTaskId) => void;
  dismissChecklist: () => void;
};

let loadPromise: Promise<void> | null = null;

/**
 * Single writer for onboarding.state. All mutations merge locally then persist
 * (debounced) — avoids read-modify-write races between UI surfaces. Concurrent
 * sessions are last-write-wins on the whole key, which is acceptable here.
 */
export const useOnboardingStore = create<OnboardingStore>()((set, get) => {
  /** Merge a partial update into state and persist. */
  const update = (
    updater: (prev: OnboardingState) => OnboardingState,
    { immediate = false }: { immediate?: boolean } = {}
  ) => {
    const prev = get().state;
    if (!prev) return;
    const next = updater(prev);
    set({ state: next });
    const save = immediate ? saveOnboardingState : saveOnboardingStateDebounced;
    Promise.resolve(save(next)).catch((error) => {
      // Persistence failures shouldn't break the flow — state is still live
      // in memory; the next successful save will catch it up.
      console.error("[onboarding] Failed to save state:", error);
    });
  };

  return {
    state: null,
    loaded: false,
    loadFailed: false,

    load: () => {
      // Idempotent: many components may call this; only fetch once.
      if (!loadPromise) {
        loadPromise = (async () => {
          try {
            const state = await getOnboardingState();
            set({ state, loaded: true });
          } catch (error) {
            console.error("[onboarding] Failed to load state:", error);
            Sentry.captureException(error);
            // Fail closed: gate treats this as "don't show onboarding".
            set({ loaded: true, loadFailed: true });
          }
        })();
      }
      return loadPromise;
    },

    begin: () => {
      if (get().state) return; // already started
      const initial = createInitialOnboardingState();
      set({ state: initial });
      trackOnboarding("started");
      saveOnboardingState(initial).catch((error) => {
        console.error("[onboarding] Failed to save initial state:", error);
      });
    },

    setAnswers: (answers) =>
      update((prev) => ({ ...prev, answers })),

    setStep: (step) => {
      trackOnboarding("step_completed", get().state?.lastStep);
      update((prev) => ({ ...prev, lastStep: step }));
    },

    setSelectedWatchlists: (ids) => {
      trackOnboarding("watchlists_selected", String(ids.length));
      update((prev) => ({ ...prev, selectedWatchlistIds: ids }));
    },

    markCompleted: (appliedTemplateId) => {
      trackOnboarding("template_applied", appliedTemplateId);
      update(
        (prev) => ({
          ...prev,
          completedAt: new Date().toISOString(),
          appliedTemplateId,
        }),
        { immediate: true }
      );
    },

    dismiss: () => {
      trackOnboarding("skipped", get().state?.lastStep);
      update(
        (prev) => ({ ...prev, dismissedAt: new Date().toISOString() }),
        { immediate: true }
      );
    },

    markTaskDone: (taskId) => {
      const tasks = get().state?.checklist?.tasks;
      if (!tasks || tasks[taskId]) return; // not started, or already done
      trackOnboarding("checklist_task_done", taskId);
      update((prev) => ({
        ...prev,
        checklist: {
          ...prev.checklist,
          tasks: {
            ...prev.checklist.tasks,
            [taskId]: new Date().toISOString(),
          },
        },
      }));
    },

    dismissChecklist: () => {
      trackOnboarding("checklist_dismissed");
      update(
        (prev) => ({
          ...prev,
          checklist: {
            ...prev.checklist,
            dismissedAt: new Date().toISOString(),
          },
        }),
        { immediate: true }
      );
    },
  };
});
